import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCheckCircle, FiAlertCircle, FiShield } = FiIcons;

const ActionableSteps = ({ hasTyphoon, signals }) => {
  const getActionableSteps = () => {
    if (!hasTyphoon) {
      return [
        'Monitor PAGASA weather updates regularly',
        'Keep emergency kit ready and accessible',
        'Stay informed about local flood-prone areas',
        'Charge devices and prepare backup power',
        'Check with barangay/DRRMO for community updates'
      ];
    }

    const hasHighSignal = signals?.some(s => parseInt(s.level) >= 3);
    
    if (hasHighSignal) {
      return [
        'EVACUATE immediately if in high-risk areas',
        'Avoid all outdoor activities and travel',
        'Secure all loose objects and board up windows',
        'Stock emergency supplies for 3-7 days',
        'Stay tuned to official evacuation orders'
      ];
    }

    return [
      'Stay alert for flood advisories and warnings',
      'Avoid coastal travel under gale warnings',
      'Charge all devices and prepare backup power',
      'Secure outdoor furniture and equipment',
      'Check barangay/DRRMO updates regularly'
    ];
  };

  const steps = getActionableSteps();
  const urgencyLevel = hasTyphoon ? (signals?.some(s => parseInt(s.level) >= 3) ? 'high' : 'medium') : 'low';

  const getHeaderColor = () => {
    switch (urgencyLevel) {
      case 'high': return 'text-red-700';
      case 'medium': return 'text-orange-700';
      default: return 'text-blue-700';
    }
  };

  const getIconColor = () => {
    switch (urgencyLevel) {
      case 'high': return 'text-red-600';
      case 'medium': return 'text-orange-600';
      default: return 'text-blue-600';
    }
  };

  const getBgColor = () => {
    switch (urgencyLevel) {
      case 'high': return 'from-red-50 to-red-100 border-red-200';
      case 'medium': return 'from-orange-50 to-orange-100 border-orange-200';
      default: return 'from-blue-50 to-blue-100 border-blue-200';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className={`bg-gradient-to-br ${getBgColor()} rounded-xl shadow-lg p-6 border`}
    >
      <div className="flex items-center mb-4">
        <div className="p-2 bg-white rounded-lg mr-3">
          <SafeIcon 
            icon={urgencyLevel === 'high' ? FiAlertCircle : FiShield} 
            className={`text-xl ${getIconColor()}`} 
          />
        </div>
        <h2 className={`text-lg font-bold ${getHeaderColor()}`}>
          Actionable Steps
        </h2>
      </div>

      <ul className="space-y-2">
        {steps.map((step, index) => (
          <motion.li
            key={index}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 * index }}
            className="flex items-start text-sm"
          >
            <SafeIcon 
              icon={FiCheckCircle} 
              className={`mr-2 mt-0.5 text-sm ${getIconColor()} flex-shrink-0`} 
            />
            <span className="text-gray-700">{step}</span>
          </motion.li>
        ))}
      </ul>

      {urgencyLevel === 'high' && (
        <div className="mt-4 p-3 bg-red-100 border border-red-300 rounded-lg">
          <p className="text-red-800 text-xs font-medium">
            ⚠️ HIGH ALERT: Follow evacuation orders immediately. Life-threatening conditions possible.
          </p>
        </div>
      )}
    </motion.div>
  );
};

export default ActionableSteps;